import api from './api';
import {
    ApiConnectionCreateRequest,
    ApiConnectionDecryptedModel,
    ApiConnectionModel,
    ApiConnectionUpdateRequest,
    ApiResponse
} from '../types/apiConnection';

export const apiConnectionService = {
    /**
     * 获取所有API连接（不含敏感字段）
     */
    getAllConnections: async (): Promise<ApiConnectionModel[]> => {
        try {
            const response = await api.get<ApiResponse<ApiConnectionModel[]>>('/api/v1/connections');

            if (!response.data.success) {
                throw new Error(response.data.message || '获取连接列表失败');
            }

            return response.data.data || [];
        } catch (error) {
            throw error;
        }
    },

    /**
     * 获取单个API连接详情（包含敏感字段）
     */
    getConnectionById: async (id: number): Promise<ApiConnectionDecryptedModel> => {
        try {
            const response = await api.get<ApiResponse<ApiConnectionDecryptedModel>>(`/api/v1/connections/${id}`);

            if (!response.data.success) {
                throw new Error(response.data.message || '获取连接详情失败');
            }

            if (!response.data.data) {
                throw new Error('获取连接详情返回数据为空');
            }

            return response.data.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * 创建API连接
     */
    createConnection: async (connection: ApiConnectionCreateRequest): Promise<ApiConnectionModel> => {
        try {
            const response = await api.post<ApiResponse<ApiConnectionModel>>('/api/v1/connections', connection);

            if (!response.data.success) {
                throw new Error(response.data.message || '创建连接失败');
            }

            if (!response.data.data) {
                throw new Error('创建连接返回数据为空');
            }

            return response.data.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * 更新API连接
     */
    updateConnection: async (id: number, connection: ApiConnectionUpdateRequest): Promise<ApiConnectionModel> => {
        try {
            const response = await api.put<ApiResponse<ApiConnectionModel>>(`/api/v1/connections/${id}`, connection);

            if (!response.data.success) {
                throw new Error(response.data.message || '更新连接失败');
            }

            if (!response.data.data) {
                throw new Error('更新连接返回数据为空');
            }

            return response.data.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * 删除API连接
     */
    deleteConnection: async (id: number): Promise<string> => {
        try {
            const response = await api.delete<ApiResponse<string>>(`/api/v1/connections/${id}`);

            if (!response.data.success) {
                throw new Error(response.data.message || '删除连接失败');
            }

            return response.data.data || '删除成功';
        } catch (error) {
            throw error;
        }
    },

    /**
     * 重置API连接：清空该 key 的历史数据（订单/仓位快照/权益/余额）并重置「key 加入系统时间」；
     * 存在运行中的永续仓位时后端拒绝（message 含原因）
     */
    resetConnection: async (id: number): Promise<{ apiKeyId: number; resetTimeMs: number } | null> => {
        try {
            const response = await api.post<ApiResponse<{ apiKeyId: number; resetTimeMs: number }>>(
                `/api/v1/connections/${id}/reset`);

            if (!response.data.success) {
                throw new Error(response.data.message || '重置连接失败');
            }

            return response.data.data ?? null;
        } catch (error) {
            throw error;
        }
    },

    /**
     * 设置默认API连接
     */
    setDefaultConnection: async (id: number): Promise<string> => {
        try {
            const response = await api.put<ApiResponse<string>>(`/api/v1/connections/${id}/default`);

            if (!response.data.success) {
                throw new Error(response.data.message || '设置默认连接失败');
            }

            return response.data.data || '设置成功';
        } catch (error) {
            throw error;
        }
    },

    /**
     * 切换API连接状态（启用/禁用）
     */
    toggleConnectionStatus: async (id: number): Promise<ApiConnectionModel> => {
        try {
            const response = await api.put<ApiResponse<ApiConnectionModel>>(`/api/v1/connections/${id}/status`);

            if (!response.data.success) {
                throw new Error(response.data.message || '切换状态失败');
            }

            return response.data.data!;
        } catch (error) {
            throw error;
        }
    },

    /**
     * 测试API连接
     */
    testConnection: async (testData: {
        exchange: string;
        apiKey: string;
        secretKey: string;
        passphrase?: string;
        endpoint: string;
    }): Promise<{ success: boolean; message: string }> => {
        try {
            const response = await api.post<ApiResponse<{
                success: boolean;
                message: string
            }>>('/api/v1/connections/test', testData);

            if (!response.data.success) {
                return {success: false, message: response.data.message || '测试失败'};
            }

            return response.data.data || {success: false, message: '未知错误'};
        } catch (error: any) {
            return {success: false, message: error.message || '测试失败'};
        }
    },

    /**
     * 通过ID测试API连接（使用存储的API信息）
     */
    testConnectionById: async (id: number): Promise<{ success: boolean; message: string }> => {
        try {
            const response = await api.post<ApiResponse<{
                success: boolean;
                message: string
            }>>(`/api/v1/connections/${id}/test`);

            if (!response.data.success) {
                return {success: false, message: response.data.message || '测试失败'};
            }

            return response.data.data || {success: false, message: '未知错误'};
        } catch (error: any) {
            return {success: false, message: error.message || '测试失败'};
        }
    },

    /**
     * 获取状态显示名称
     */
    getStatusDisplayName: (status: string): string => {
        switch (status) {
            case 'active':
                return '活跃';
            case 'inactive':
                return '禁用';
            default:
                return status || '未知';
        }
    },

    /**
     * 设置全局展示API连接（仅管理员）
     */
    setGlobalDisplayConnection: async (id: number): Promise<string> => {
        try {
            const response = await api.put<ApiResponse<string>>(`/api/v1/connections/${id}/global-display`);
            if (!response.data.success) {
                throw new Error(response.data.message || '设置失败');
            }
            return response.data.data || '设置成功';
        } catch (error) {
            throw error;
        }
    },

    /**
     * 取消全局展示API连接（仅管理员）
     */
    cancelGlobalDisplayConnection: async (id: number): Promise<string> => {
        try {
            const response = await api.put<ApiResponse<string>>(`/api/v1/connections/${id}/global-display/cancel`);
            if (!response.data.success) {
                throw new Error(response.data.message || '取消失败');
            }
            return response.data.data || '取消成功';
        } catch (error) {
            throw error;
        }
    },

    /**
     * 获取全局展示API连接
     */
    getGlobalDisplayConnection: async (): Promise<{
        apiKeyId: number | null;
        exchange: string | null;
        keyName: string | null
    }> => {
        try {
            const response = await api.get<ApiResponse<{
                apiKeyId: number;
                exchange: string;
                keyName: string
            }>>('/api/v1/trading/global/connection');
            if (!response.data.success) {
                return {apiKeyId: null, exchange: null, keyName: null};
            }
            return response.data.data || {apiKeyId: null, exchange: null, keyName: null};
        } catch {
            return {apiKeyId: null, exchange: null, keyName: null};
        }
    },

    /**
     * 格式化显示掩码
     */
    maskSensitiveValue: (value: string): string => {
        if (!value || value.length <= 8) {
            return '****';
        }
        return value.substring(0, 4) + '****' + value.substring(value.length - 4);
    }
};

export default apiConnectionService;
